/**
 * Единый перехват ухода со страницы с несохранёнными изменениями.
 *
 * Раньше механизм назывался `unsavedMetricsChanges` и знал ровно про экран
 * показателей: флаг в `sessionStorage` + `confirmMetricsPageLeave()`, который
 * спрашивали `Header`, `HeaderStudioSelector` и `StudioNavigation`. Форма
 * отсмотра защищалась своим, третьим способом — перехватом кликов по
 * `a[href]`, — а таких элементов во всём UI три штуки: вся навигация
 * приложения это кнопки с `navigate()`. Поэтому 8–15 минут заполнения
 * терялись от одного промаха по нижней навигации УК
 * (AUDIT_PEDAGOGY_UX_2026-08-21.md, P0 «Длинная форма отсмотра теряется молча»).
 *
 * Здесь не заводится ЧЕТВЁРТЫЙ механизм: экран показателей остаётся на своём
 * флаге (он обязан переживать размонтирование страницы), а страницы со
 * сложным состоянием и собственным текстом вопроса регистрируют колбэк.
 * Обе оси сходятся в `confirmLeaveUnsavedChanges()` — единственной функции,
 * которую спрашивает навигация.
 *
 * ⚠️ `useBlocker` из react-router здесь неприменим: он требует data-роутера
 * (`createBrowserRouter`), а приложение смонтировано на `<BrowserRouter>` —
 * вызов падает в `useDataRouterContext`. Перевод роутера это отдельная задача
 * с радиусом на всё приложение.
 */

const UNSAVED_METRICS_KEY = 'metrics_page_has_unsaved_changes'

/**
 * Колбэки страниц. `Set`, а не одиночный слот: две страницы одновременно
 * смонтированными не бывают, но забытая регистрация не должна молча
 * вытеснять чужую — она должна быть видна как лишний вопрос, а не как
 * пропавшая защита.
 */
const guards = new Set<() => boolean>()

export function setHasUnsavedMetricsChanges(value: boolean) {
  if (typeof window === 'undefined') return

  if (value) {
    sessionStorage.setItem(UNSAVED_METRICS_KEY, '1')
    return
  }

  sessionStorage.removeItem(UNSAVED_METRICS_KEY)
}

export function hasUnsavedMetricsChanges() {
  if (typeof window === 'undefined') return false
  return sessionStorage.getItem(UNSAVED_METRICS_KEY) === '1'
}

/**
 * Регистрирует проверку ухода со страницы. Колбэк возвращает `false`, чтобы
 * отменить переход (обычно — после отказа в `window.confirm`).
 *
 * Возвращает функцию снятия: вызывать в cleanup эффекта, иначе guard
 * размонтированной страницы продолжит спрашивать.
 */
export function registerUnsavedChangesGuard(guard: () => boolean): () => void {
  guards.add(guard)
  return () => {
    guards.delete(guard)
  }
}

/**
 * Единственная точка, которую спрашивает навигация перед переходом.
 * `true` — уходить можно.
 *
 * Порядок важен: сначала зарегистрированные колбэки (у них свой текст вопроса
 * и своё состояние), потом флаг экрана показателей. Первый отказ прекращает
 * опрос — второй подряд `window.confirm` пользователь читать не станет.
 */
export function confirmLeaveUnsavedChanges(): boolean {
  for (const guard of guards) {
    if (!guard()) return false
  }

  if (!hasUnsavedMetricsChanges()) return true
  return window.confirm('У вас есть несохраненные изменения. Вы уверены, что хотите выйти без сохранения?')
}
